import { useState, useEffect } from "react";
import { T, STATE, getStateConfig } from "../../config/theme.js";
import { api, trackEvent } from "../../hooks/useApi.js";
import { Card, Btn, Input, StateBadge, SectionTitle } from "../ui/index.jsx";

export default function Journey({ token, onBack }) {
  const [actions, setActions] = useState([]);
  const [goals, setGoals] = useState(null);
  const [editGoals, setEditGoals] = useState(false);
  const [goalForm, setGoalForm] = useState({ purpose: "", risk_tolerance: "중간", monthly_target: "" });

  useEffect(() => {
    // Load from DB
    api("/actions", {}, token).then(data => setActions(data || [])).catch(() => {
      // fallback: localStorage
      const saved = JSON.parse(localStorage.getItem("phase_actions") || "[]");
      setActions(saved);
    });
    api("/actions/goals", {}, token).then(data => {
      if (data) { setGoals(data); setGoalForm(data); }
    }).catch(() => {
      const savedGoals = JSON.parse(localStorage.getItem("phase_goals") || "null");
      if (savedGoals) { setGoals(savedGoals); setGoalForm(savedGoals); }
    });
  }, []);

  const saveGoals = () => {
    api("/actions/goals", { method: "PUT", body: JSON.stringify(goalForm) }, token)
      .then(data => { setGoals(data || goalForm); setEditGoals(false); })
      .catch(() => {
        // fallback
        localStorage.setItem("phase_goals", JSON.stringify(goalForm));
        setGoals(goalForm); setEditGoals(false);
      });
  };

  // Stats
  const totalActions = actions.length;
  const phaseAligned = actions.filter(a => a.alignment === "phase").length;
  const alignRate = totalActions > 0 ? ((phaseAligned / totalActions) * 100).toFixed(0) : 0;
  const profits = actions.filter(a => a.action_type === "profit");
  const cuts = actions.filter(a => a.action_type === "cut");
  const holds = actions.filter(a => a.action_type === "hold_plan");
  const cautionActions = actions.filter(a => a.phase_state === "CAUTION" || a.phase_state === "PAUSE");

  // Realized PnL
  const sellActions = actions.filter(a => a.realized_pnl != null);
  const totalRealizedPnl = sellActions.reduce((sum, a) => sum + (a.realized_pnl || 0), 0);
  const totalSellAmount = sellActions.reduce((sum, a) => sum + ((a.avg_price || 0) * (a.sell_qty || 0)), 0);
  const totalRealizedPnlPct = totalSellAmount > 0 ? ((totalRealizedPnl / totalSellAmount) * 100).toFixed(1) : 0;

  // Phase-aligned vs personal PnL
  const phaseAlignedPnl = sellActions.filter(a => a.alignment === "phase").reduce((s, a) => s + (a.realized_pnl || 0), 0);
  const personalPnl = sellActions.filter(a => a.alignment === "personal").reduce((s, a) => s + (a.realized_pnl || 0), 0);

  // Days since first action
  const firstAction = actions.length > 0 ? new Date(actions[actions.length - 1].created_at) : null;
  const daysSince = firstAction ? Math.floor((Date.now() - firstAction.getTime()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <Btn variant="ghost" onClick={onBack}>← 돌아가기</Btn>
      </div>

      <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 8 }}>나의 투자 여정</h2>
      <p style={{ fontSize: 14, color: T.textDim, marginBottom: 28 }}>Pause와 함께한 기록을 확인하세요</p>

      {/* Goal Setting */}
      <Card style={{ marginBottom: 20, borderLeft: `3px solid ${T.accent}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>나의 투자 목표</div>
          <Btn variant="ghost" style={{ fontSize: 11, padding: "4px 12px" }} onClick={() => setEditGoals(!editGoals)}>{editGoals ? "취소" : goals ? "수정" : "설정하기"}</Btn>
        </div>
        {editGoals ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 4 }}>투자 목적 (왜 주식을 하나요?)</div>
              <Input placeholder="예: 결혼자금 모으기, 노후 준비" value={goalForm.purpose} onChange={e => setGoalForm({ ...goalForm, purpose: e.target.value })} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 4 }}>위험 감수 성향</div>
              <div style={{ display: "flex", gap: 8 }}>
                {["보수적", "중간", "적극적"].map(r => (
                  <div key={r} onClick={() => setGoalForm({ ...goalForm, risk_tolerance: r })} style={{
                    flex: 1, padding: "8px", borderRadius: 8, textAlign: "center", cursor: "pointer", fontSize: 12, fontWeight: 600,
                    background: goalForm.risk_tolerance === r ? T.accent + "20" : T.bg, border: `1px solid ${goalForm.risk_tolerance === r ? T.accent : T.border}`,
                    color: goalForm.risk_tolerance === r ? T.accent : T.textDim,
                  }}>{r}</div>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 4 }}>월 목표 수익률 (%)</div>
              <Input placeholder="예: 3" type="number" value={goalForm.monthly_target} onChange={e => setGoalForm({ ...goalForm, monthly_target: e.target.value })} />
            </div>
            <Btn variant="primary" onClick={saveGoals} style={{ justifyContent: "center" }}>목표 저장</Btn>
            <div style={{ fontSize: 11, color: T.textMuted, textAlign: "center" }}>※ 목표 달성을 보장하지 않습니다. 목표 기준에서 현재 상태와 리스크를 안내합니다.</div>
          </div>
        ) : goals ? (
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div><div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>투자 목적</div><div style={{ fontSize: 13, fontWeight: 600 }}>{goals.purpose || "미설정"}</div></div>
            <div><div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>위험 성향</div><div style={{ fontSize: 13, fontWeight: 600 }}>{goals.risk_tolerance}</div></div>
            <div><div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>월 목표</div><div style={{ fontSize: 13, fontWeight: 600 }}>{goals.monthly_target ? `${goals.monthly_target}%` : "미설정"}</div></div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: T.textMuted }}>투자 목표를 설정하면 Pause가 목표 기준으로 안내합니다</div>
        )}
      </Card>

      {/* KPI Summary */}
      <div className="phase-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Pause 사용 기간", value: `${daysSince}일`, color: T.accent },
          { label: "총 실현 손익", value: `${totalRealizedPnl >= 0 ? "+" : ""}${totalRealizedPnl.toLocaleString()}원`, color: totalRealizedPnl >= 0 ? T.green : T.red },
          { label: "실현 수익률", value: `${totalRealizedPnlPct >= 0 ? "+" : ""}${totalRealizedPnlPct}%`, color: +totalRealizedPnlPct >= 0 ? T.green : T.red },
          { label: "행동 일치율", value: `${alignRate}%`, color: +alignRate >= 70 ? T.green : T.yellow },
          { label: "수익 실현", value: `${profits.length}건`, color: T.green },
          { label: "손절", value: `${cuts.length}건`, color: T.red },
          { label: "위험 시 행동", value: `${cautionActions.length}건`, color: T.blue },
          { label: "총 행동 기록", value: `${totalActions}건`, color: T.text },
        ].map((kpi, i) => (
          <Card key={i}>
            <div style={{ fontSize: 13, color: T.textSub, marginBottom: 6, fontWeight: 600 }}>{kpi.label}</div>
            <div style={{ fontSize: kpi.value.length > 12 ? 16 : 22, fontWeight: 900, color: kpi.color, wordBreak: "break-all" }}>{kpi.value}</div>
          </Card>
        ))}
      </div>

      {/* Phase vs Personal Comparison */}
      {sellActions.length > 0 && (
        <Card style={{ marginBottom: 20, borderLeft: `3px solid ${T.yellow}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Pause 신호 기반 vs 개인 판단 비교</div>
          <div className="phase-compare" style={{ display: "flex", gap: 20 }}>
            <div style={{ flex: 1, padding: "14px", background: T.bg, borderRadius: 10, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: T.textSub, marginBottom: 6, fontWeight: 600 }}>Pause 신호 기반</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: phaseAlignedPnl >= 0 ? T.green : T.red }}>
                {phaseAlignedPnl >= 0 ? "+" : ""}{phaseAlignedPnl.toLocaleString()}원
              </div>
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>{sellActions.filter(a => a.alignment === "phase").length}건</div>
            </div>
            <div style={{ flex: 1, padding: "14px", background: T.bg, borderRadius: 10, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: T.textSub, marginBottom: 6, fontWeight: 600 }}>개인 판단</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: personalPnl >= 0 ? T.green : T.red }}>
                {personalPnl >= 0 ? "+" : ""}{personalPnl.toLocaleString()}원
              </div>
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>{sellActions.filter(a => a.alignment === "personal").length}건</div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, textAlign: "center", marginTop: 10 }}>※ 사용자가 기록한 매도 내역 기준입니다</div>
        </Card>
      )}

      {/* Action Timeline */}
      <Card>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>행동 기록 타임라인</div>
        {actions.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: T.textMuted }}>
            <div style={{ fontSize: 13 }}>아직 기록이 없습니다</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>종목 카드에서 행동을 기록해보세요</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {actions.slice(0, 20).map((a, i) => {
              const actionLabel = { profit: "수익 실현", cut: "손절", hold_plan: "계획 유지", add_buy: "분할매수", active_hold: "관망 유지", partial_sell: "일부 매도" };
              const alignLabel = { phase: "Pause 신호 기반", personal: "개인 판단", mixed: "복합적" };
              const sc = getStateConfig(a.phase_state);
              const deleteAction = () => {
                if (!confirm("이 기록을 삭제할까요?")) return;
                const actionId = a.id;
                if (actionId) {
                  api(`/actions/${actionId}`, { method: "DELETE" }, token)
                    .then(() => setActions(prev => prev.filter(x => x.id !== actionId)))
                    .catch(() => {
                      // fallback
                      const saved = JSON.parse(localStorage.getItem("phase_actions") || "[]");
                      saved.splice(i, 1);
                      localStorage.setItem("phase_actions", JSON.stringify(saved));
                      setActions([...saved]);
                    });
                } else {
                  const saved = JSON.parse(localStorage.getItem("phase_actions") || "[]");
                  saved.splice(i, 1);
                  localStorage.setItem("phase_actions", JSON.stringify(saved));
                  setActions([...saved]);
                }
              };
              return (
                <div key={i} style={{ padding: "12px 14px", background: T.bg, borderRadius: 10, borderLeft: `3px solid ${sc.color}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{a.name}</span>
                      <span style={{ fontSize: 11, color: sc.color, fontWeight: 600 }}>{sc.icon} {sc.label}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: T.textMuted }}>{new Date(a.created_at).toLocaleDateString("ko-KR")}</span>
                      <span onClick={deleteAction} style={{ fontSize: 11, color: T.red, cursor: "pointer", opacity: 0.6, transition: "opacity 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                        onMouseLeave={e => e.currentTarget.style.opacity = "0.6"}>✕</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 12, color: T.textDim, flexWrap: "wrap" }}>
                    <span>{actionLabel[a.action_type] || a.action_type}</span>
                    {a.alignment && <span>· {alignLabel[a.alignment]}</span>}
                    {a.realized_pnl != null && (
                      <span style={{ color: a.realized_pnl >= 0 ? T.green : T.red, fontWeight: 700 }}>
                        · {a.realized_pnl >= 0 ? "+" : ""}{a.realized_pnl.toLocaleString()}원 ({a.realized_pnl_pct >= 0 ? "+" : ""}{a.realized_pnl_pct?.toFixed(1)}%)
                      </span>
                    )}
                    {a.sell_qty && <span>· {a.sell_qty}주 × {a.sell_price?.toLocaleString()}원</span>}
                    {a.action_type === "add_buy" && a.buy_qty && (
                      <span style={{ color: T.accent, fontWeight: 600 }}>· {a.buy_qty}주 × {a.avg_price?.toLocaleString()}원 → 새 평단가 {a.new_avg_price?.toLocaleString()}원</span>
                    )}
                  </div>
                  {a.memo && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{a.memo}</div>}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div style={{ textAlign: "center", fontSize: 11, color: T.textMuted, marginTop: 20 }}>
        ※ 이 페이지는 행동 기반 기록입니다. Pause가 수익을 보장하지 않습니다.
      </div>
    </div>
  );
}
